import type { OnInit } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, ElementRef, computed, inject } from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';

import {
    EButtonStyleType,
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
} from '@common/enums';

import type { EduLoginButtonEntity, EduLoginButtonItem } from '@sites/entity-core';
import { flexAlignVar } from '@sites/entity-core';
import type { EduLoginButtonForm, EduLoginButtonZeroForm } from '@sites/entity-signal-form';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import {
    DEFAULT_BUTTON_CLASS,
    BUTTON_TYPE_CLASS_MAP,
    BUTTON_SIZE_CLASS_MAP,
} from '@web/widgets/common/base/base-button/base-button.const';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

import {
    BUTTON_OUTER_MARGIN,
    CONTAINER_INNER_MARGIN,
    getButtonItemCssClasses,
    getButtonItemCssVars,
} from './edu-login-button-view.css-vars';

/**
 * Editor preview for the `edu-login-button` widget — a fork of the button
 * widget's view. The form is shared with the control panel via
 * `WebLegacyWidgetSignalFormService` (ref-counted by widget id).
 */
@Component({
    selector: 'web-edu-login-button-view',
    templateUrl: './edu-login-button-view.component.html',
    styleUrls: ['./edu-login-button-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
})
export class EduLoginButtonViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<EduLoginButtonEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly elementRef: ElementRef = inject(ElementRef);
    private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);

    public form!: EduLoginButtonForm;

    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    /** True when the widget lives inside a zero block. Read through `value()`. */
    public get isZero(): boolean {
        return this.form?.value().type === 'zero';
    }

    private readonly data = computed<EduLoginButtonEntity['data']>(() => this.form.value().data);
    private readonly isZeroWidget = computed<boolean>(() => this.form.value().type === 'zero');

    // --- CSS custom property values (bound on the group wrapper in the template) ---
    public readonly groupZIndex = computed<string | null>(() => {
        const zIndex = this.data()?.position?.zIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly groupZIndexMobile = computed<string | null>(() => {
        const data = this.data();
        const zIndex = data?.isSynchronizeStyle ? data?.position?.zIndex : data?.position?.mobileZIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly groupTransform = computed<string>(() =>
        this.isZeroWidget() ? 'none' : `rotate(${this.data()?.position?.rotate ?? 0}deg)`
    );
    public readonly groupTransformMobile = computed<string>(() =>
        this.isZeroWidget() ? 'none' : `rotate(${this.data()?.position?.mobileRotate ?? 0}deg)`
    );
    public readonly groupMargin = computed<string>(() =>
        this.isZeroWidget() ? '0' : CONTAINER_INNER_MARGIN[this.data().horizontalIndent]
    );
    public readonly itemMargin = computed<string>(() =>
        this.isZeroWidget() ? '0' : BUTTON_OUTER_MARGIN[this.data().horizontalIndent]
    );
    public readonly groupJustify = computed<string>(() => flexAlignVar(this.data().horizontalAlign));
    public readonly groupJustifyMobile = computed<string>(() =>
        flexAlignVar(this.data().mobileHorizontalAlign ?? this.data().horizontalAlign)
    );
    public readonly groupJustifyTablet = computed<string>(() =>
        flexAlignVar(this.data().tabletHorizontalAlign ?? this.data().horizontalAlign)
    );

    // Per-item variable maps, one entry per rendered button.
    public readonly buttonsCssVars = computed(() => {
        const widget = this.form.value() as WebLegacyWidgetEntity<EduLoginButtonEntity['data']>;
        return widget.data.buttons.map((button: EduLoginButtonItem) => getButtonItemCssVars(widget, button));
    });

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<EduLoginButtonForm>(this.widget().id, this.destroyRef);

        if (this.isZero) {
            this.setupZeroHeightSync();
        }
    }

    /**
     * Zero-block buttons auto-grow with their content: the rendered anchor's
     * height is mirrored back into the extended-position grid so the zero
     * host's selection frame and boundary math track the real size.
     */
    private setupZeroHeightSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => this.syncZeroHeightFromDom(),
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    private syncZeroHeightFromDom(): void {
        const anchor = this.elementRef.nativeElement.querySelector('a') as HTMLAnchorElement | null;
        if (!anchor) return;

        const height = parseFloat(getComputedStyle(anchor).height);
        if (!Number.isFinite(height) || height <= 0) return;

        const zeroForm = this.form as EduLoginButtonZeroForm;
        const position = zeroForm.position().value();

        if (this.isDeviceMobileSig()) {
            if (position.mobileHeight !== height) zeroForm.position.mobileHeight().value.set(height);
            return;
        }

        if (this.isDeviceTabletSig()) {
            if (position.tabletHeight !== height) zeroForm.position.tabletHeight().value.set(height);
            return;
        }

        const writeAll = () => {
            if (position.mobileHeight !== height) zeroForm.position.mobileHeight().value.set(height);
            if (position.tabletHeight !== height) zeroForm.position.tabletHeight().value.set(height);
            if (position.height !== height) zeroForm.position.height().value.set(height);
        };

        if (zeroForm.inTransaction()) {
            writeAll();
        } else {
            zeroForm.transact(writeAll);
        }
    }

    public getButtonEffectClass(button: EduLoginButtonItem): string {
        let nameEffect: string = '';

        if (button.additionalEffects?.isActive) {
            if (button.additionalEffects.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (button.additionalEffects.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (button.additionalEffects.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (button.additionalEffects.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public getButtonClass(button: EduLoginButtonItem): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[button.size]}`;

        if (button.styleType === EButtonStyleType.custom) {
            return `${styles} ${getButtonItemCssClasses(button)} ${this.getButtonEffectClass(button)}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[button.styleType as keyof typeof BUTTON_TYPE_CLASS_MAP]} ${getButtonItemCssClasses(
            button
        )} ${this.getButtonEffectClass(button)}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }
}
